import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FileText, ReceiptText, Download, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatMoney } from '@/lib/api';
import { useI18n } from '@/context/I18nContext';

const STATUS = {
    issued: 'bg-emerald-100 text-emerald-800',
    mock: 'bg-amber-100 text-amber-800',
    cancelled: 'bg-rose-100 text-rose-800',
};

export default function InvoicesPage() {
    const { t } = useI18n();
    const [status, setStatus] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [orders, setOrders] = useState([]);
    const [open, setOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [form, setForm] = useState({ receiver_rfc: '', receiver_name: '', receiver_email: '', cfdi_use: 'G03', payment_form: '01', payment_method: 'PUE', postal_code: '', tax_regime: '612' });
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [s, inv, purch] = await Promise.all([
            api.get('/factura/config-status'),
            api.get('/me/invoices'),
            api.get('/me/orders/purchases'),
        ]);
        setStatus(s.data);
        setInvoices(inv.data || []);
        setOrders((purch.data || []).filter(o => o.payment_status === 'paid'));
    };
    useEffect(() => { load(); }, []);

    const openIssue = (order) => { setSelectedOrder(order); setOpen(true); };
    const submit = async () => {
        if (!/^[A-Za-z0-9Ññ&]{10,13}$/.test(form.receiver_rfc)) { toast.error('RFC inválido'); return; }
        if (!form.receiver_name || form.receiver_name.length < 2) { toast.error('Nombre requerido'); return; }
        if (!/^\d{4,5}$/.test(form.postal_code)) { toast.error('Código postal inválido'); return; }
        setBusy(true);
        try {
            const { data } = await api.post('/invoices/issue', { order_id: selectedOrder.id, ...form });
            if (data.status === 'mock') toast.warning('Factura generada en MODO SIMULACIÓN (Factura.com sin configurar).');
            else toast.success('Factura emitida exitosamente.');
            setOpen(false); await load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'No se pudo emitir'); }
        finally { setBusy(false); }
    };

    return (
        <div className="space-y-4" data-testid="invoices-page">
            <div className="flex items-center gap-2">
                <ReceiptText className="h-6 w-6 text-red-600" />
                <h1 className="text-2xl font-semibold">{t('invoice.title','Facturas CFDI')}</h1>
            </div>
            {status && !status.configured && (
                <div className="p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-amber-600" />
                    Factura.com está en <strong>MODO SIMULACIÓN</strong>. El Superadmin debe configurar las claves para emitir CFDI reales.
                </div>
            )}
            <Card>
                <CardHeader><CardTitle>Órdenes elegibles para factura</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    {orders.length === 0 && <div className="text-sm text-muted-foreground py-4">Sin órdenes pagadas.</div>}
                    {orders.map(o => {
                        const invoiced = invoices.find(i => i.order_id === o.id && i.status !== 'cancelled');
                        return (
                            <div key={o.id} className="flex items-center justify-between border rounded-lg p-3" data-testid={`invoice-order-${o.id}`}>
                                <div>
                                    <div className="font-medium">{o.ad_snapshot?.title || o.ad_id}</div>
                                    <div className="text-xs text-muted-foreground">Orden {o.id.slice(0,8)} • {formatMoney(o.amount || 0)}</div>
                                </div>
                                {invoiced ? <Badge className={STATUS[invoiced.status] || ''}>{invoiced.status === 'mock' ? 'Simulada' : invoiced.status}</Badge> : <Button size="sm" variant="outline" onClick={() => openIssue(o)} data-testid={`invoice-issue-${o.id}`}><FileText className="h-4 w-4 mr-1" />{t('invoice.issue','Solicitar factura')}</Button>}
                            </div>
                        );
                    })}
                </CardContent>
            </Card>

            <Card>
                <CardHeader><CardTitle>Mis facturas</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                    {invoices.length === 0 && <div className="text-sm text-muted-foreground py-4">Aún no tienes facturas.</div>}
                    {invoices.map(i => (
                        <div key={i.id} className="flex items-center justify-between border rounded-lg p-3">
                            <div>
                                <div className="font-medium">{i.serie}-{i.folio || i.uid?.slice(0,8)}</div>
                                <div className="text-xs text-muted-foreground">{i.receiver_name} • {i.receiver_rfc} • {formatMoney(i.total)}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge className={STATUS[i.status] || ''}>{i.status === 'mock' ? 'Simulada' : i.status}</Badge>
                                {i.pdf_url && <a href={i.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-red-600 inline-flex items-center gap-1"><Download className="h-3 w-3" />PDF</a>}
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Emitir factura CFDI</DialogTitle></DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div><Label>{t('invoice.receiver_rfc','RFC del receptor')}</Label><Input value={form.receiver_rfc} onChange={e => setForm({...form, receiver_rfc: e.target.value.toUpperCase()})} data-testid="invoice-rfc" /></div>
                        <div><Label>Código postal</Label><Input value={form.postal_code} onChange={e => setForm({...form, postal_code: e.target.value})} data-testid="invoice-cp" /></div>
                        <div className="col-span-2"><Label>{t('invoice.receiver_name','Nombre / razón social')}</Label><Input value={form.receiver_name} onChange={e => setForm({...form, receiver_name: e.target.value})} data-testid="invoice-name" /></div>
                        <div className="col-span-2"><Label>Email (opcional)</Label><Input value={form.receiver_email} onChange={e => setForm({...form, receiver_email: e.target.value})} /></div>
                        <div><Label>Uso CFDI</Label><Select value={form.cfdi_use} onValueChange={v => setForm({...form, cfdi_use: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="G01">G01 Adquisición</SelectItem><SelectItem value="G03">G03 Gastos en general</SelectItem><SelectItem value="P01">P01 Por definir</SelectItem></SelectContent></Select></div>
                        <div><Label>Régimen fiscal</Label><Select value={form.tax_regime} onValueChange={v => setForm({...form, tax_regime: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="612">612 PF con Act. Empresarial</SelectItem><SelectItem value="605">605 Sueldos y salarios</SelectItem><SelectItem value="626">626 RESICO PF</SelectItem><SelectItem value="601">601 General de Ley PM</SelectItem></SelectContent></Select></div>
                        <div><Label>Forma de pago</Label><Select value={form.payment_form} onValueChange={v => setForm({...form, payment_form: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="01">01 Efectivo</SelectItem><SelectItem value="03">03 Transferencia</SelectItem><SelectItem value="04">04 Tarjeta de crédito</SelectItem><SelectItem value="28">28 Tarjeta de débito</SelectItem></SelectContent></Select></div>
                        <div><Label>Método pago</Label><Select value={form.payment_method} onValueChange={v => setForm({...form, payment_method: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="PUE">PUE Pago en una sola exhibición</SelectItem><SelectItem value="PPD">PPD Parcialidades</SelectItem></SelectContent></Select></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>{t('action.cancel','Cancelar')}</Button>
                        <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="invoice-submit">{busy ? '…' : 'Emitir factura'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
